import { yupResolver } from "@hookform/resolvers/yup"
import PropTypes from "prop-types"
import React, { useContext, useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useMutation } from "react-query"

import Button from "src/components/Button"
import Form from "src/components/Form"
import Tooltip from "src/components/Tooltip"

import {
  createContractGroup,
  createContractQuote,
  updateContractGroup,
  updateContractQuote,
} from "src/api/Contracts"

import ApplyToQuotesModal from "./ApplyToQuotesModal"
import { ContractsFormContext } from "./ContractsFormContext"
import schema from "./schema"
import { getInitialValues, setSubmitParams } from "./utils"
import FormWizard from "./wizard/FormWizard"
import WizardContextProvider from "./wizard/WizardContext"

const ContractsForm = ({
  attachments,
  backUrl,
  contact,
  contactBoat,
  copyableContractsCount,
  totalCompletedContracts,
  duplicateId,
  form: defaultFormValues,
  outstandingCount,
  title,
  unsentCount,
  view,
}) => {
  const {
    groupId,
    action,
    selectedTemplateId,
    withTemplate,
    isGroup,
    templates,
    useNewTaxExperience,
  } = useContext(ContractsFormContext)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const defaultValues = useMemo(
    () =>
      getInitialValues(
        defaultFormValues,
        attachments,
        contact,
        contactBoat,
        isGroup,
        selectedTemplateId,
        withTemplate,
        templates
      ),
    [
      defaultFormValues,
      attachments,
      contact,
      contactBoat,
      isGroup,
      selectedTemplateId,
      withTemplate,
      templates,
    ]
  )

  const form = useForm({
    defaultValues,
    resolver: yupResolver(schema),
    mode: "onChange",
  })

  const getEndpoint = (data) => {
    const formattedBaseParams = setSubmitParams(
      data,
      useNewTaxExperience,
      isGroup
    )
    if (isGroup) {
      // we're saving a contract group
      const params = {
        ...formattedBaseParams,
        name: data.name,
        ...(data.copyBoats ? { copy_boats: data.copyBoats } : {}),
      }
      if (!defaultFormValues?.id || duplicateId) {
        return createContractGroup(params, duplicateId)
      }
      return updateContractGroup(params, defaultFormValues.id)
    } else {
      // we're saving a contract quote
      const params = {
        ...formattedBaseParams,
        contact_id: data.contact_id,
        contact_boat_id: data.contact_boat_id,
      }
      if (!defaultFormValues?.id) {
        return createContractQuote(params, groupId)
      }
      return updateContractQuote(params, groupId)
    }
  }

  const { mutate, isLoading } = useMutation(
    [isGroup ? "contractGroup" : "contractQuote", defaultFormValues?.id],
    getEndpoint,
    {
      // eslint-disable-next-line camelcase
      onSuccess: ({ redirect_to }) => {
        window.location.assign(new URL(redirect_to, window.location.origin))
      },
      onError: (error) => {
        form.setError("root.serverError", {
          message: error.message,
        })
      },
    }
  )

  const shouldShowModal =
    (unsentCount > 0 || outstandingCount > 0) && isGroup && view === "edit"

  const handleSubmitForm = (data) => {
    if (shouldShowModal) {
      setIsModalOpen(true)
    } else {
      mutate(data)
    }
  }

  const duplicateBoatTooltipText = () => (
    <span>
      Only <strong className="text-teal-300">signed</strong> and{" "}
      <strong className="text-teal-300">manually completed</strong> contracts
      will be copied. Excludes{" "}
      <strong>unsigned, unsent, declined, canceled, voided, deleted,</strong>{" "}
      and those tied to <strong>archived contacts.</strong>
    </span>
  )

  return (
    <div className="container">
      <FormProvider {...form}>
        <Form
          onSubmit={form.handleSubmit(handleSubmitForm)}
          autocomplete={false}
        >
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 flex items-center justify-between border border-teal-400 bg-teal-100 p-4">
              <div className="ml-2 font-semibold">{title}</div>
              <div className="flex flex-row items-center justify-end gap-x-4">
                {form.formState.errors?.root?.serverError ? (
                  <Form.Error>
                    {form.formState.errors.root.serverError.message}
                  </Form.Error>
                ) : null}
                {duplicateId && copyableContractsCount > 0 && (
                  <Tooltip
                    text={duplicateBoatTooltipText()}
                    maxWidth="400px"
                    placement="bottom"
                    variant="dark"
                  >
                    <Form.Checkbox
                      {...form.register("copyBoats")}
                      compact
                      label={`Copy ${copyableContractsCount} of ${totalCompletedContracts} contracts`}
                    />
                  </Tooltip>
                )}
                <Button
                  onClick={() => location.replace(backUrl)}
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isLoading}>
                  {action}
                </Button>
                {form?.errors?.root?.serverError ? (
                  <Form.Error>
                    {form.errors.root.serverError.message}
                  </Form.Error>
                ) : null}
              </div>
            </div>
            <WizardContextProvider>
              <FormWizard title={title} action={action} />
            </WizardContextProvider>
          </div>
        </Form>
        <ApplyToQuotesModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={form.handleSubmit(mutate)}
          isLoading={isLoading}
          unsentCount={unsentCount}
          outstandingCount={outstandingCount}
        />
      </FormProvider>
    </div>
  )
}

ContractsForm.propTypes = {
  form: PropTypes.object,
  title: PropTypes.string.isRequired,
  duplicateId: PropTypes.string,
  copyableContractsCount: PropTypes.number,
  totalCompletedContracts: PropTypes.number,
  backUrl: PropTypes.string.isRequired,
  attachments: PropTypes.array,
  contact: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }),
  contactBoat: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }),
  selectedTemplateId: PropTypes.string,
  unsentCount: PropTypes.number,
  outstandingCount: PropTypes.number,
  withTemplate: PropTypes.bool,
  view: PropTypes.string.isRequired,
}

export default ContractsForm
